/**
 * Sub-admin capability catalog — single source of truth for the frontend.
 *
 * The backend's authoritative list lives in `services/capabilities.py`.
 * This file mirrors it for UI purposes only (labels, descriptions,
 * grouping). Both User Management (change-role modal) and the Admin
 * Docs Manager (per-article visibility picker) import from here so a
 * new capability shows up in every relevant admin surface with one edit.
 *
 * When you add a new capability to the backend `ALL_CAPS` list:
 *   1. Add the key + title + description here in the appropriate group
 *      (or start a new group if it's an entirely new module).
 *   2. Update any capability-check gates on the backend routes and
 *      frontend `hasCapability(...)` call sites.
 * Nothing else needs to change — every consumer picks it up automatically.
 */

export const CAP_GROUPS = [
  {
    module: 'Content',
    caps: [
      { key: 'manage_content',          title: 'Manage Content',          desc: 'Edit approved questions, Picture Manager, Notes, Forum, Written CRUD' },
      { key: 'review_user_submissions', title: 'Review User Submissions', desc: 'Approve/reject/merge questions from the pending queue (awards coins)' },
      { key: 'manage_bulk_import',      title: 'Manage Bulk Import',      desc: 'Review staging, edit, bulk-approve to live, batch nuke (upload stays admin-only)' },
    ],
  },
  {
    module: 'User Management',
    caps: [
      { key: 'manage_users',     title: 'Manage Users',     desc: 'Edit info, reset password (regular users only)' },
      { key: 'change_user_plan', title: 'Change User Plan', desc: 'Change subscription plan (regular users only)' },
    ],
  },
  {
    module: 'Live Classes',
    caps: [
      { key: 'host_classes',   title: 'Host Classes',   desc: 'Take classes assigned by an admin' },
      { key: 'manage_classes', title: 'Manage Classes', desc: 'Schedule, edit, publish + host (no Zoom API settings)' },
    ],
  },
  {
    module: 'Finances & Analytics',
    caps: [
      { key: 'view_finances',  title: 'View Finances',  desc: 'Sales overview, payment logs (no plans, no Razorpay settings)' },
      { key: 'view_analytics', title: 'View Analytics', desc: 'Platform-wide engagement analytics' },
    ],
  },
  {
    module: 'Admin Tools',
    caps: [
      { key: 'manage_updates', title: 'Manage Updates', desc: 'Send announcements from the Updates page' },
      { key: 'grant_coins',    title: 'Grant Coins',    desc: 'Manual coin credit — reward/refund users' },
      { key: 'view_sessions',  title: 'View Sessions',  desc: 'Monitor active user sessions' },
    ],
  },
];

/** Flat list of every valid capability key across all groups. */
export const ALL_SUB_ADMIN_CAPS = CAP_GROUPS.flatMap((g) => g.caps.map((c) => c.key));

/** Mirror of the backend CAP_IMPLIES map. Auto-check + lock rule:
 *  when the key is on, every value cap is implicitly on too. */
export const CAP_IMPLIES = { manage_classes: ['host_classes'] };

/** Fast key → label lookup used when rendering capability chips outside
 *  the picker (e.g., "Visible to: Manage Bulk Import + Review Submissions"). */
export const CAP_LABEL = Object.fromEntries(
  CAP_GROUPS.flatMap((g) => g.caps.map((c) => [c.key, c.title]))
);
